import { useEffect, useState } from 'react';
import { setBookingOverlapReviewer, type BookingOverlapWindow } from '@cdf/api-client-react';
import { createBookingOverlapController, type OverlapReview } from '@/lib/booking-overlap-controller';
import { formatDateTimeRange } from '@/lib/site-time';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

function windowLabel(window: BookingOverlapWindow) {
  return formatDateTimeRange(window.start, window.end, { kind: 'site', timeZone: window.timezone }, { includeZone: true });
}

/** Shared by generated single, itinerary, series and assistant creation hooks. */
export function BookingOverlapReview() {
  const [review, setReview] = useState<OverlapReview | null>(null);
  useEffect(() => {
    const controller = createBookingOverlapController(setReview);
    setBookingOverlapReviewer(controller.review);
    return () => {
      setBookingOverlapReviewer(null);
      controller.cancel();
    };
  }, []);

  return <Dialog open={Boolean(review)} onOpenChange={(open) => { if (!open) review?.decide(false); }}>
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>Review overlapping bookings</DialogTitle>
        <DialogDescription>
          {review?.warning.beneficiaryName} will have overlapping reservations for different spaces of the same type.
          Choose Book anyway if these reservations are intentional.
        </DialogDescription>
      </DialogHeader>
      <div className="max-h-[55vh] space-y-3 overflow-y-auto">
        {review?.warning.overlaps.map(({ proposed, overlapping }) => (
          <div key={proposed.proposedIndex + ':' + (overlapping.bookingId ?? 'proposal-' + overlapping.proposedIndex)}
            className="space-y-1 rounded-md border p-3 text-sm">
            <p className="font-medium">{proposed.spaceTypeName}</p>
            <p>Proposed: {proposed.spaceName}</p>
            <p className="text-muted-foreground">{windowLabel(proposed)} ({proposed.timezone})</p>
            <p>{overlapping.bookingId ? 'Existing reservation' : 'Also in this proposal'}: {overlapping.spaceName}</p>
            <p className="text-muted-foreground">{windowLabel(overlapping)} ({overlapping.timezone})</p>
          </div>
        ))}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => review?.decide(false)}>Go back</Button>
        <Button onClick={() => review?.decide(true)}>Book anyway</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
